import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import Clipboard from 'clipboard';

// Actions
import { hideModal } from 'cards/actions/modalActions';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';

// Components
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import TextareaFieldV2 from '@zola/zola-ui/src/components/Form/TextareaFieldV2/TextareaFieldV2';
import WildcardColorPicker from 'cards/components/common/WildcardColorPicker/WildcardColorPicker';
import DSTDEmailPreview from 'cards/components/common/modals/Digital/DSTDEmailTemplate/DSTDEmailPreview';

// Utils
import { DSTDEmailTemplateDetails } from 'cards/components/Customization/DigitalSteps/Share/shareStepUtils';

import styles from '../styles.module.less';
import DSTDEmailTemplate from './DSTDEmailTemplate';

const CHAR_COUNT_SHORT = 150;
const CHAR_COUNT_LONG = 250;

type DigitalEmailTemplateCustomizationModalProps = {
  dstdSlug: string;
  templateDetails: DSTDEmailTemplateDetails;
};

const DigitalEmailTemplateCustomizationModal = (
  props: DigitalEmailTemplateCustomizationModalProps
): JSX.Element => {
  const templateRef = useRef(null);
  const { dstdSlug, templateDetails } = props;
  const {
    ownerAndPartnerNames,
    digitalDetails: {
      wedding_website_slug: weddingWebsiteSlug,
      super_link_slug: superLinkSlug,
      customizations: {
        // @ts-ignore
        0: {
          digital_details: {
            details: { background_color: bgColor },
          },
        },
      },
    },
  } = templateDetails;

  const dispatch = useDispatch();

  let clipboard: any = null;

  const [title, setTitle] = useState<string>(
    `Save the Date for ${ownerAndPartnerNames}'s Wedding!`
  );
  const [titleColor, setTitleColor] = useState<string>('000000');

  const [weddingWebsiteText, setWeddingWebsiteText] = useState<string>(
    `For details, check out ${ownerAndPartnerNames}'s wedding website.`
  );
  const [websiteColor, setWebsiteColor] = useState<string>('7f7f7f');

  const [superLinkText, setSuperLinkText] = useState<string>(
    `Come celebrate! 🎉
Share your address to receive an invitation.`
  );
  const [superLinkColor, setSuperLinkColor] = useState<string>('7f7f7f');

  const initializeClipboard = () => {
    if (clipboard) clipboard.destroy();
    clipboard = null;

    const cb = new Clipboard('#copy-email-template-modal', {
      target: () => {
        return templateRef.current;
      },
    });
    cb.on('success', () => {
      dispatch(toastsActions.positive({ headline: 'Email template copied' }));
    });

    cb.on('error', () => {
      dispatch(toastsActions.negative({ headline: 'Could not copy your email template' }));
    });

    clipboard = cb;
  };

  const onConfirm = () => {
    dispatch(hideModal());
  };

  useEffect(() => {
    initializeClipboard();

    return () => {
      if (clipboard) clipboard.destroy();
      clipboard = null;
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>Customize your email</div>
      <div className={cx(styles.content, styles.templateModalContent)}>
        <div className={styles.optionsWrapper}>
          <div>
            <h4>Title</h4>
            <div className={styles.emailFieldWrapper}>
              <div>
                <TextareaFieldV2
                  value={title}
                  onChange={e => setTitle(e.currentTarget.value)}
                  maxLength={CHAR_COUNT_SHORT}
                />
                <span className={styles.fieldCharCount}>
                  {title.length}/{CHAR_COUNT_SHORT}
                </span>
              </div>
              <WildcardColorPicker color={titleColor} setColor={setTitleColor} />
            </div>
          </div>

          {superLinkSlug && (
            <>
              <hr />
              <div>
                <h4>Super Link</h4>
                <div className={styles.emailFieldWrapper}>
                  <div>
                    <TextareaFieldV2
                      value={superLinkText}
                      onChange={e => setSuperLinkText(e.currentTarget.value)}
                      maxLength={CHAR_COUNT_LONG}
                    />
                    <span className={styles.fieldCharCount}>
                      {superLinkText.length}/{CHAR_COUNT_LONG}
                    </span>
                  </div>
                  <WildcardColorPicker color={superLinkColor} setColor={setSuperLinkColor} />
                </div>
              </div>
            </>
          )}

          {weddingWebsiteSlug && (
            <>
              <hr />
              <div>
                <h4>Wedding Website</h4>
                <div className={styles.emailFieldWrapper}>
                  <div>
                    <TextareaFieldV2
                      value={weddingWebsiteText}
                      onChange={e => setWeddingWebsiteText(e.currentTarget.value)}
                      maxLength={CHAR_COUNT_LONG}
                    />
                    <span className={styles.fieldCharCount}>
                      {weddingWebsiteText.length}/{CHAR_COUNT_LONG}
                    </span>
                  </div>
                  <WildcardColorPicker color={websiteColor} setColor={setWebsiteColor} />
                </div>
              </div>
            </>
          )}
        </div>
        <div className={styles.templateWrapper}>
          <DSTDEmailPreview
            {...{
              title,
              dstdSlug,
              weddingWebsiteSlug,
              weddingWebsiteText,
              superLinkSlug,
              superLinkText,
              titleColor,
              websiteColor,
              superLinkColor,
              bgColor,
            }}
          />
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <ButtonV3 id="copy-email-template-modal" onClick={onConfirm}>
          Copy Your Template
        </ButtonV3>
      </div>

      <DSTDEmailTemplate
        ref={templateRef}
        bgColor={bgColor}
        customizationData={{
          titleData: { text: title, color: titleColor },
          dstdSlug,
          ...(weddingWebsiteSlug && {
            websiteData: {
              text: weddingWebsiteText,
              slug: weddingWebsiteSlug,
              color: websiteColor,
            },
          }),
          ...(superLinkSlug && {
            superLinkData: { text: superLinkText, slug: superLinkSlug, color: superLinkColor },
          }),
        }}
      />
    </div>
  );
};

export default DigitalEmailTemplateCustomizationModal;
